import { installPlatform } from '~/platform/index.js';
import { once } from '~/session/singleFlight.js';

/**
 * Swaps the web platform for the Capacitor one, when there is a Capacitor to
 * swap to.
 *
 * `main.tsx` calls `initPlatform()` once at startup. In a browser tab that
 * call has to be free and silent — no plugin chunk fetched, no import of
 * `@capacitor/core`, nothing thrown — which is why the detection reads the
 * bridge object the native runtime injects instead of importing the SDK to ask
 * it. Everything native lives behind the dynamic import below, so the web
 * bundle never contains a byte of it.
 */

interface CapacitorBridge {
  isNativePlatform?: () => boolean;
}

/** True only inside an iOS/Android Capacitor WebView. */
export function isNativeShell(): boolean {
  try {
    // Double assertion because `globalThis` has no declared `Capacitor`; the
    // native runtime adds it before the bundle evaluates.
    const bridge = (globalThis as unknown as { Capacitor?: CapacitorBridge }).Capacitor;
    return bridge?.isNativePlatform?.() === true;
  } catch {
    // A host that booby-traps `globalThis.Capacitor` is not a host we are
    // running natively in.
    return false;
  }
}

/** The one bootstrap, so every caller awaits the same swap rather than racing it. */
const ready = once(bootstrap);

/**
 * Never rejects: a shell that cannot load its plugins still has to play.
 *
 * Idempotent and shared, because it is also the readiness signal. `main.tsx`
 * fires it and does not wait, so on a cold native launch the Capacitor chunk is
 * still being fetched while `restore()` is already registering the device —
 * and `platform().name` still answers `'web'`, which is then stamped on the
 * device row once and never revisited. Anything whose answer depends on which
 * adapter is installed awaits this first.
 */
export function initPlatform(): Promise<void> {
  return ready();
}

async function bootstrap(): Promise<void> {
  if (!isNativeShell()) return;

  try {
    const { createCapacitorPlatform } = await import('~/platform/capacitor.js');
    const native = await createCapacitorPlatform();
    installPlatform(native);
    // Phones are portrait-only (capacitor.config.ts explains where that is
    // really enforced); asking the WebView as well costs nothing and covers
    // Android builds whose manifest allows rotation on tablets.
    await native.lockPortrait();
  } catch (error) {
    console.warn('[platform] native bootstrap failed; keeping the web adapter', error);
  }
}
